import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid'
import { enUS, frFR } from '@mui/x-data-grid/locales'
import ConfirmActionButton from '@src/shared/components/ConfirmActionButton'
import { useTickets, useCloseRelatedErrors } from '@src/tools/error-tracker/api/tickets-api'
import { useToast } from '@src/shared/hooks/useToast'
import { reportErrorToConsole } from '@src/shared/services/error-logger'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTicketsColumns } from './tickets-hooks'

const TicketsPage = () => {
  const { t, i18n } = useTranslation()
  const [tickets, isLoading, error] = useTickets()
  const { showSuccess, showError, ToastComponent } = useToast()
  const [closeRelatedErrors, isClosing] = useCloseRelatedErrors()
  const [selectedTicketIds, setSelectedTicketIds] = useState<GridRowSelectionModel>([])

  const columns = useTicketsColumns()

  const localeText =
    i18n.language === 'fr'
      ? frFR.components.MuiDataGrid.defaultProps.localeText
      : enUS.components.MuiDataGrid.defaultProps.localeText

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{t('tickets.errorLoading')}</Alert>
      </Box>
    )
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        {t('tickets.title')}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('tickets.description')}
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('tickets.selectedCount', { count: selectedTicketIds.length })}
        </Typography>
        <ConfirmActionButton
          variant="contained"
          color="primary"
          loading={isClosing}
          isError={false}
          disabled={selectedTicketIds.length === 0}
          confirmText={t('tickets.confirmCloseErrors')}
          onConfirm={async () => {
            try {
              const result = await closeRelatedErrors({
                  ticketIds: selectedTicketIds as string[]
              })
              showSuccess(t('tickets.closeErrorsSuccess', { count: result.totalErrorsClosed }))
              setSelectedTicketIds([])
            } catch (error) {
              showError(t('tickets.closeErrorsError'))
              reportErrorToConsole(error)
            }
          }}
          sx={{ minWidth: 200 }}
        >
          {t('tickets.closeRelatedErrors')}
        </ConfirmActionButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 400 }}>
        <DataGrid
          rows={tickets || []}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
            sorting: {
              sortModel: [{ field: 'closedDate', sort: 'desc' }],
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          checkboxSelection
          rowSelectionModel={selectedTicketIds}
          onRowSelectionModelChange={setSelectedTicketIds}
          disableRowSelectionOnClick
          localeText={localeText}
          sx={{
            flex: 1,
            border: 'none',
            '.MuiDataGrid-columnHeaders div:first-of-type': {
              bgcolor: 'secondary.main',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '8px 8px 0 0',
            },
            '.MuiDataGrid-cell': {
              fontWeight: 500,
            },
            '.MuiDataGrid-sortIcon': { color: 'white' },
          }}
        />
      </Box>
      <ToastComponent />
    </Box>
  )
}

export default TicketsPage
